<template>
  <AppFormGroup>
    <SubHeading keypath="headings.address" />

    <div v-if="applicantType === ApplicantType.JOINT" class="pt-6">
      <UIInputCheckbox
        id="copyPrimaryAddress"
        v-model="copyPrimaryAddress"
        :label="t('copyPrimaryAddress.label')"
      />
    </div>

    <div class="flex flex-col">
      <AppField :path-prefix="applicantType" path="homeAddress.line1" />
    </div>

    <div v-if="addressLine2Response" class="grid grid-cols-1 md:gap-x-4">
      <AppField
        v-if="shouldShowAddressLine2"
        :path-prefix="applicantType"
        path="homeAddress.line2"
      />

      <a
        v-else
        class="text-primary-600 place-self-start hover:cursor-pointer hover:underline"
        @click.prevent="showAddressLine2()"
      >
        <i18n-t keypath="buttons.show_line2" tag="span" class="flex-1">
          <template #icon>
            <FontAwesomeIcon :icon="faPlus" />
          </template>
        </i18n-t>
      </a>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 md:gap-x-4">
      <AppField
        v-model="zipCode"
        :path-prefix="applicantType"
        :disabled="fetchingZip"
        path="homeAddress.zipCode"
        class="larger-help md:basis-1/2"
      />
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 md:gap-x-4">
      <AppField
        v-model="city"
        :path-prefix="applicantType"
        :disabled="fetchingZip"
        path="homeAddress.city"
        class="md:basis-1/2"
      />
      <AppField
        v-model="state"
        :path-prefix="applicantType"
        :disabled="fetchingZip"
        path="homeAddress.state"
        class="md:basis-1/2"
      />
    </div>

    <div class="flex flex-col md:flex-row md:gap-x-4">
      <AppField
        :path-prefix="applicantType"
        path="housingStatus"
        class="md:basis-3/5"
      />

      <AppField
        :path-prefix="applicantType"
        path="housingRentMortgageCents"
        class="md:basis-2/5"
      />

      <AppField
        :path-prefix="applicantType"
        path="housingYears"
        class="md:basis-2/5"
      />
    </div>
  </AppFormGroup>
</template>

<script setup lang="ts">
  import {
    AppField,
    AppFormGroup,
    useAppFieldModel,
  } from "@versatile-credit-apply/components/form"

  import { ApplicantType } from "@versatile-credit-apply/omni-apply-client"

  import { faPlus } from "@fortawesome/pro-regular-svg-icons"
  import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"

  import {
    useProviderI18n,
    useFetchZipCodeMetadata,
    useAppFieldResponse,
  } from "@versatile-credit-apply/sdk"
  import { ref, toRaw, watch } from "vue"
  import type { FormSectionProps } from "./form-section-api"

  const props = defineProps<FormSectionProps>()

  const { t } = useProviderI18n(() => props.providerKey)

  const shouldShowAddressLine2 = ref(false)

  const addressLine2Response = useAppFieldResponse(
    `${props.applicantType}.homeAddress.line2`
  )

  /*
    City/State Autofill
   */
  const city = useAppFieldModel(() => `${props.applicantType}.homeAddress.city`)
  const state = useAppFieldModel(
    () => `${props.applicantType}.homeAddress.state`
  )

  const zipCode = useAppFieldModel(
    () => `${props.applicantType}.homeAddress.zipCode`
  )
  const fetchingZip = useFetchZipCodeMetadata(zipCode, (metadata) => {
    city.value = metadata.city
    state.value = metadata.state
  })

  /*
    Copy Address
   */
  const copyPrimaryAddress = ref(false)

  const primaryHomeAddress = useAppFieldModel("primary.homeAddress")
  const jointHomeAddress = useAppFieldModel("joint.homeAddress")

  watch(copyPrimaryAddress, (shouldCopy) => {
    const primaryAddress = toRaw(primaryHomeAddress.value)

    if (shouldCopy && primaryAddress) {
      jointHomeAddress.value = structuredClone(primaryAddress)

      if (jointHomeAddress.value?.line2 != undefined) {
        showAddressLine2()
      }
    } else {
      jointHomeAddress.value = undefined
      hideAddressLine2()
    }
  })

  const showAddressLine2 = () => {
    shouldShowAddressLine2.value = true
  }

  const hideAddressLine2 = () => {
    shouldShowAddressLine2.value = false
  }
</script>

<script lang="ts">
  import type { ApplicationFieldPath } from "@versatile-credit-apply/omni-apply-client"

  export const HomeAddressPrimaryFields: Array<ApplicationFieldPath> = [
    "primary.homeAddress.line1",
    "primary.homeAddress.line2",
    "primary.homeAddress.city",
    "primary.homeAddress.state",
    "primary.homeAddress.zipCode",
    "primary.housingStatus",
    "primary.housingRentMortgageCents",
    "primary.housingYears",
  ]

  export const HomeAddressJointFields: Array<ApplicationFieldPath> = [
    "joint.homeAddress.line1",
    "joint.homeAddress.line2",
    "joint.homeAddress.city",
    "joint.homeAddress.state",
    "joint.homeAddress.zipCode",
    "joint.housingStatus",
    "joint.housingRentMortgageCents",
    "joint.housingYears",
  ]
</script>

<i18n lang="yaml" locale="en">
appFields:
  housingRentMortgageCents:
    label: Monthly Rent/Mortgage
  housingYears:
    label: Years At Address
  liveWithSpouse:
    label: Same Address
  zipCode:
    description: Enter ZIP Code to see City and State
buttons:
  show_line2: >
    {icon} Add Apt., Suite, Unit, etc.
copyPrimaryAddress:
  label: Use primary Applicant's address

headings:
  address: Address Information
</i18n>

<i18n lang="yaml" locale="easypay_standard_en">
appFields:
  housingRentMortgageCents:
    label: Monthly Housing Payment
</i18n>

<i18n lang="yaml" locale="tdbank_standard_en">
appFields:
  housingRentMortgageCents:
    description: If you pay rent, please enter your monthly amount, if not, please enter zero.
  line1:
    description: |
      Physical Address, no P.O. Boxes. You must be a resident of the United States
      to apply.
</i18n>

<style scoped>
  @reference "@/main.css";

  .larger-help :deep(small:not(.error-text)) {
    @apply text-base;
  }
</style>
